import { Box, Button, LinearProgress, useTheme } from '@mui/material';
import { useLazyQuery, useQuery } from '@parkchirp/api-client';
import { getSequencingSets } from '@parkchirp/lease';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
	GET_COUNTS_AND_RATES_FOR_SEQUENCING,
	GET_FUTURE_PARKERS,
	GET_RATES_AND_SUBLOTS_FOR_BASE_TIERS,
	GET_RESEQUENCE_IN_PROGRESS,
	GET_SEQUENCING_TIERS,
} from '../../../queries';
import { SetShowDialog, SetShowSnackbar } from '../../../types';
import { BaseTierRate, FutureParker, SequencingCountAndRate, SequencingTier } from '../../../types/leases';
import { AddBaseTierModal } from './addBaseTierModal';
import { AddSequencingTierModal } from './addSequencingTierModal';
import { SequencingSet } from './sequencingSet';

type SequencingProps = {
	subscriptionId: string;
	setShowSnackbar: SetShowSnackbar;
	setShowDialog: SetShowDialog;
};

export const Sequencing: FC<SequencingProps> = ({ subscriptionId, setShowSnackbar, setShowDialog }) => {
	const [allTiers, setAllTiers] = useState<SequencingTier[]>([]);
	const [maxSequenceNumber, setMaxSequenceNumber] = useState<number>();
	const [futureParkers, setFutureParkers] = useState<FutureParker[]>([]);
	const [countsAndRates, setCountsAndRates] = useState<SequencingCountAndRate[]>([]);
	const [baseTierRates, setBaseTierRates] = useState<BaseTierRate[]>([]);
	const [loadQuietly, setLoadQuietly] = useState(false);

	const theme = useTheme();

	const sequencingTierSets = useMemo(() => getSequencingSets(allTiers), [allTiers]);
	const maxSetNumber = useMemo(() => sequencingTierSets[0]?.[0].setNumber ?? 0, [sequencingTierSets]);

	const getSequencingTiersQuery = useQuery(GET_SEQUENCING_TIERS, {
		variables: { subscriptionId },
		context: { apiVersion: 'v2' },
		notifyOnNetworkStatusChange: true,
		onCompleted: (data) => {
			const { sequencingTiers: tierData, maxSequenceNumber: maxNumber } = data.getSequencingTiers;

			setAllTiers(tierData);
			setMaxSequenceNumber(maxNumber);
		},
	});
	const { loading: tierDataLoading, refetch: refetchTiers } = getSequencingTiersQuery;

	const { loading: futureParkersLoading, refetch: refetchFutureParkers } = useQuery(GET_FUTURE_PARKERS, {
		variables: { subscriptionId },
		context: { apiVersion: 'v2' },
		notifyOnNetworkStatusChange: true,
		onCompleted: (data) => {
			setFutureParkers(data.getFutureParkers?.parkers);
		},
	});

	const [getCountsAndRatesForSequencing, { loading: countsAndRatesLoading }] = useLazyQuery(
		GET_COUNTS_AND_RATES_FOR_SEQUENCING,
		{ fetchPolicy: 'network-only' },
	);

	const [getRatesAndSublotsForBaseTiers, { loading: baseTierRatesLoading }] = useLazyQuery(
		GET_RATES_AND_SUBLOTS_FOR_BASE_TIERS,
		{ fetchPolicy: 'network-only' },
	);

	const [checkResequencingInProgress, { loading: resequencingInProgress, stopPolling: stopResequencePolling }] =
		useLazyQuery(GET_RESEQUENCE_IN_PROGRESS, { fetchPolicy: 'network-only' });

	const pollSequencingTiers = useCallback(async () => {
		setLoadQuietly(true);

		for (let i = 1; i <= 5; i++) {
			await Promise.all([refetchTiers(), refetchFutureParkers()]);
			await new Promise((resolve) => {
				setTimeout(resolve, 2000);
			});
		}

		setLoadQuietly(false);
	}, [refetchFutureParkers, refetchTiers, setLoadQuietly]);

	const handleWaitForResequencing = useCallback(
		async (showSnackbar = true) => {
			try {
				await checkResequencingInProgress({
					variables: { subscriptionId },
					context: { apiVersion: 'v2' },
					pollInterval: 2000,
					notifyOnNetworkStatusChange: true,
					onCompleted: async ({ getResequenceSubscriptionInProgress: inProgress }) => {
						if (!inProgress) {
							if (showSnackbar) {
								setShowSnackbar({
									message: 'Resequencing completed',
									severity: 'success',
									autoHideDuration: 3000,
								});
							}
							stopResequencePolling();

							await Promise.all([refetchTiers(), refetchFutureParkers()]);
						}
					},
				});
			} catch {
				setShowSnackbar({
					message: 'Error during resequencing',
					severity: 'error',
					autoHideDuration: 5000,
				});
			}
		},
		[
			checkResequencingInProgress,
			subscriptionId,
			stopResequencePolling,
			refetchTiers,
			refetchFutureParkers,
			setShowSnackbar,
		],
	);

	useEffect(() => {
		getRatesAndSublotsForBaseTiers({
			variables: { subscriptionId },
			context: { apiVersion: 'v2' },
			onCompleted: (data: any) => {
				setBaseTierRates(data.getRatesAndSublotsForBaseTiers?.rates);
			},
		});

		getCountsAndRatesForSequencing({
			variables: { subscriptionId },
			context: { apiVersion: 'v2' },
			onCompleted: (data: any) => {
				setCountsAndRates(data.getCountsAndRatesForSequencing?.countsAndRates);
			},
		});
	}, [allTiers, subscriptionId, getCountsAndRatesForSequencing, getRatesAndSublotsForBaseTiers]);

	if (tierDataLoading && !loadQuietly) {
		return <LinearProgress sx={{ width: '100%' }} />;
	}

	if (!sequencingTierSets.length) {
		return (
			<Box>
				<Button
					onClick={() => {
						setShowDialog(
							<AddSequencingTierModal
								subscriptionId={subscriptionId}
								countsAndRates={countsAndRates}
								tiers={[]}
								setNumber={1}
								theme={theme}
								setShowSnackbar={setShowSnackbar}
								handleClose={() => setShowDialog(false)}
								pollSequencingTiers={pollSequencingTiers}
							/>,
						);
					}}
					sx={{ marginTop: '1em' }}
				>
					Add Sequencing Tier
				</Button>
				<Button
					onClick={() => {
						setShowDialog(
							<AddBaseTierModal
								subscriptionId={subscriptionId}
								rates={baseTierRates}
								theme={theme}
								setNumber={1}
								setShowSnackbar={setShowSnackbar}
								handleClose={() => setShowDialog(false)}
								pollSequencingTiers={pollSequencingTiers}
							/>,
						);
					}}
					sx={{ marginTop: '1em' }}
				>
					Add Base Tier
				</Button>
			</Box>
		);
	}

	return (
		<>
			{sequencingTierSets.map((sequencingTierSet) => (
				<SequencingSet
					subscriptionId={subscriptionId}
					allTiers={sequencingTierSet}
					setNumber={sequencingTierSet[0].setNumber}
					maxSetNumber={maxSetNumber}
					maxSequenceNumber={maxSequenceNumber}
					futureParkers={futureParkers.filter(({ tierId }) =>
						sequencingTierSet.some((tier) => tier.id === tierId),
					)}
					countsAndRates={countsAndRates}
					baseTierRates={baseTierRates}
					resequencingInProgress={resequencingInProgress}
					tierDataLoading={tierDataLoading}
					countsAndRatesLoading={countsAndRatesLoading}
					baseTierRatesLoading={baseTierRatesLoading}
					futureParkersLoading={futureParkersLoading}
					loadQuietly={loadQuietly}
					handleWaitForResequencing={handleWaitForResequencing}
					pollSequencingTiers={pollSequencingTiers}
					setShowSnackbar={setShowSnackbar}
					setShowDialog={setShowDialog}
					key={sequencingTierSet[0].id}
				/>
			))}
		</>
	);
};
